import { useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'

import { useChallenge, useUser } from '../../context'
import {
  calculateMonthlyGamification,
  challengeOverlapsRange,
  countEventsWithinRange,
  countUniqueDaysWithinRange,
  ProgressDateRange,
} from '../services/progressCalculations'
import { getPracticeDatesByDateRange } from '../services/progressRepository'

import type {
  ProgressPeriod,
  ProgressTrendPoint,
} from '../pages/Dasboard/_components/types'

type DateRange = ProgressDateRange

const getPeriodRange = (period: ProgressPeriod): DateRange => {
  const now = dayjs()

  if (period === 'previous-month') {
    const previousMonth = now.subtract(1, 'month')
    return {
      start: previousMonth.startOf('month'),
      end: previousMonth.endOf('month'),
    }
  }

  if (period === 'six-months') {
    return {
      start: now.subtract(5, 'month').startOf('month'),
      end: now.endOf('day'),
    }
  }

  if (period === 'year') {
    return {
      start: now.subtract(11, 'month').startOf('month'),
      end: now.endOf('day'),
    }
  }

  return {
    start: now.startOf('month'),
    end: now.endOf('month'),
  }
}

const createBuckets = (
  period: ProgressPeriod,
  range: DateRange,
): DateRange[] => {
  const buckets: DateRange[] = []
  let cursor = range.start
  const groupByMonth = period === 'six-months' || period === 'year'

  while (cursor.valueOf() <= range.end.valueOf()) {
    const bucketEnd = groupByMonth
      ? cursor.endOf('month')
      : cursor.add(6, 'day').endOf('day')

    buckets.push({
      start: cursor,
      end: bucketEnd.valueOf() > range.end.valueOf() ? range.end : bucketEnd,
    })
    cursor = groupByMonth
      ? cursor.add(1, 'month').startOf('month')
      : cursor.add(7, 'day').startOf('day')
  }

  return buckets
}

export const useGamificationProgress = (period: ProgressPeriod) => {
  const { user } = useUser()
  const { challengeList } = useChallenge()
  const [practiceDates, setPracticeDates] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const selectedRange = useMemo(() => getPeriodRange(period), [period])
  const currentMonthRange = useMemo(() => getPeriodRange('current-month'), [])
  const previousMonthRange = useMemo(() => getPeriodRange('previous-month'), [])
  const queryRange = useMemo(() => {
    const selectedOrCurrentStart =
      selectedRange.start.valueOf() < currentMonthRange.start.valueOf()
        ? selectedRange.start
        : currentMonthRange.start

    return {
      start:
        selectedOrCurrentStart.valueOf() < previousMonthRange.start.valueOf()
          ? selectedOrCurrentStart
          : previousMonthRange.start,
      end:
        selectedRange.end.valueOf() > currentMonthRange.end.valueOf()
          ? selectedRange.end
          : currentMonthRange.end,
    }
  }, [currentMonthRange, previousMonthRange, selectedRange])

  useEffect(() => {
    if (!user?.uid) {
      setPracticeDates([])
      setError(null)
      return
    }

    let active = true
    setLoading(true)

    getPracticeDatesByDateRange(
      user.uid,
      queryRange.start.format('YYYY-MM-DD'),
      queryRange.end.format('YYYY-MM-DD'),
    )
      .then((dates) => {
        if (active) {
          setPracticeDates(dates)
          setError(null)
        }
      })
      .catch((error) => {
        console.error('Error fetching progress logs:', error)
        if (active) {
          setPracticeDates([])
          setError(
            'No pudimos cargar tus prácticas. El resto del progreso sigue disponible.',
          )
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [queryRange, user?.uid])

  const loginDates = useMemo(
    () => (user?.logins ?? []).map((timestamp) => dayjs(timestamp.toDate())),
    [user?.logins],
  )
  const parsedPracticeDates = useMemo(
    () => practiceDates.map((date) => dayjs(date)),
    [practiceDates],
  )
  const participantChallenges = useMemo(
    () =>
      user?.uid
        ? challengeList
            .filter((challenge) => challenge.participants?.includes(user.uid))
            .map((challenge) => ({
              startDate: challenge.startDate,
              endDate: challenge.endDate,
            }))
        : [],
    [challengeList, user?.uid],
  )

  const summarizeRange = (range: DateRange) => {
    const activity = countUniqueDaysWithinRange(loginDates, range)
    const practices = countEventsWithinRange(parsedPracticeDates, range)
    const sharedChallenges = participantChallenges.filter((challenge) =>
      challengeOverlapsRange(challenge, range),
    ).length

    return {
      activity,
      practices,
      sharedChallenges,
    }
  }

  const selectedSummary = summarizeRange(selectedRange)
  const currentMonthSummary = summarizeRange(currentMonthRange)
  const previousMonthSummary = summarizeRange(previousMonthRange)
  const trend = createBuckets(period, selectedRange).map<ProgressTrendPoint>(
    (bucket) => {
      const summary = summarizeRange(bucket)
      const value =
        summary.activity + summary.practices + summary.sharedChallenges

      return {
        label:
          period === 'six-months' || period === 'year'
            ? bucket.start.format('MMM')
            : `${bucket.start.date()}–${bucket.end.date()}`,
        value,
      }
    },
  )

  return {
    ...selectedSummary,
    trend,
    gamification: calculateMonthlyGamification(
      currentMonthSummary,
      previousMonthSummary,
    ),
    error,
    hasActivity:
      currentMonthSummary.activity > 0 ||
      currentMonthSummary.practices > 0 ||
      currentMonthSummary.sharedChallenges > 0,
    loading,
  }
}
